import { Box, Typography } from "@mui/material"
import type { BiothreatSignal } from "@phc/common/gen/threatAnalyticsGen"
import type React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import MapGl, {
  Marker,
  NavigationControl,
  type MapRef,
} from "react-map-gl/mapbox"
import "mapbox-gl/dist/mapbox-gl.css"
import { MAPBOX_ACCESS_TOKEN } from "../../../utils/env"
import { usePrecomputedBiothreatSignals } from "../PrecomputedBiothreatSignalsContext"
import type { PrecomputedScanMode, PrecomputedTab } from "../types"
import { getGdeltImpactFloor, getImpact } from "./biothreatV2Signal"
import {
  groupSignalsByLocation,
  summarizeDiseases,
  type LocationGroup,
} from "./biothreatV2Groups"
import { resolveSignalLatLon } from "./biothreatV2Geo"
import {
  V2_ACCENT,
  V2_FONT_MONO,
  V2_ON_ACCENT,
  V2_SURFACE,
  V2_TEXT,
} from "./biothreatV2Tokens"

/** Repo-standard dark Mapbox style (shared with BioMesh). */
const MAP_STYLE = "mapbox://styles/thepublichealthco/cmmavy6eg00c801su7td45kh3"

// Default GDELT view: fully zoomed out so the whole globe is visible.
const WORLD_VIEW = { longitude: 0, latitude: 20, zoom: 0 }
// CONUS: zoomed out enough to frame the full mainland (~58° of longitude).
const CONUS_VIEW = { longitude: -96, latitude: 38.5, zoom: 2.3 }

/** Seconds for the scan line to cross the map once (slow, deliberate sweep). */
const SWEEP_PERIOD_S = 10
/** On-demand passes are a quick, deliberate scan rather than the ambient sweep. */
const ON_DEMAND_SWEEP_PERIOD_S = 5
/** Leading-edge glow trailing to the right of the on-demand scan line. */
const SWEEP_GLOW = `linear-gradient(90deg, rgba(55,178,255,0.32) 0%, rgba(55,178,255,0.12) 45%, rgba(55,178,255,0) 100%)`

// Reveal animation timing (ms). The green entrance and red burn-off are driven
// per-frame from the rAF loop (no CSS transitions), so these own their easing.
const ENTRANCE_HOLD_MS = 160
const ENTRANCE_FADE_MS = 520
const REJECT_HOLD_MS = 110
const REJECT_FADE_MS = 520

const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3

const hexToRgb = (hex: string): [number, number, number] => {
  const v = hex.replace("#", "")
  const full =
    v.length === 3
      ? v
          .split("")
          .map(c => c + c)
          .join("")
      : v
  const n = Number.parseInt(full, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

/** Interpolate between two hex colors, returning an `rgb(...)` string. */
const lerpColor = (from: string, to: string, t: number): string => {
  const [r1, g1, b1] = hexToRgb(from)
  const [r2, g2, b2] = hexToRgb(to)
  const r = Math.round(r1 + (r2 - r1) * t)
  const g = Math.round(g1 + (g2 - g1) * t)
  const b = Math.round(b1 + (b2 - b1) * t)
  return `rgb(${r}, ${g}, ${b})`
}

/**
 * Single writer for a dot's animated styles. Passing `undefined` for background
 * or boxShadow clears the inline value so the dot falls back to its sx baseline
 * (the severity-blue color + glow).
 */
const styleDot = (
  el: HTMLButtonElement,
  opts: {
    opacity: number
    scale: number
    filter: string
    background?: string
    boxShadow?: string
  }
): void => {
  el.style.opacity = String(opts.opacity)
  el.style.transform = `translate(-50%, -50%) scale(${opts.scale})`
  el.style.filter = opts.filter
  el.style.backgroundColor = opts.background ?? ""
  el.style.boxShadow = opts.boxShadow ?? ""
  el.style.pointerEvents = opts.opacity === 0 ? "none" : "auto"
}

/** Restores a dot to its sx baseline by clearing every inline style we mutate. */
const clearDotStyles = (el: HTMLButtonElement): void => {
  el.style.opacity = ""
  el.style.transform = ""
  el.style.filter = ""
  el.style.backgroundColor = ""
  el.style.boxShadow = ""
  el.style.pointerEvents = ""
}

const idSetOf = (rows: BiothreatSignal[]): Set<string> =>
  new Set(rows.map(row => row.id))

/** Dedupe-by-id union, so a transition can render both states' dots at once. */
const unionRows = (
  a: BiothreatSignal[],
  b: BiothreatSignal[]
): BiothreatSignal[] => {
  const byId = new Map<string, BiothreatSignal>()
  for (const row of a) byId.set(row.id, row)
  for (const row of b) byId.set(row.id, row)
  return Array.from(byId.values())
}

const Hud: React.FC<{ children: React.ReactNode; sx?: object }> = ({
  children,
  sx,
}) => (
  <Typography
    sx={{
      position: "absolute",
      fontFamily: V2_FONT_MONO,
      fontSize: 12,
      letterSpacing: "0.1em",
      fontWeight: 600,
      color: V2_TEXT.muted,
      pointerEvents: "none",
      zIndex: 5,
      ...sx,
    }}
  >
    {children}
  </Typography>
)

const LocationMarker: React.FC<{
  group: LocationGroup
  selected: boolean
  /**
   * GDELT impact floor (null for NWSS). The map groups the full noise field for
   * the sweep, so the hover label is filtered to signals at/above the floor —
   * otherwise sub-threshold clusters would show in a dot the filter excludes.
   */
  threshold: number | null
  /** Opens the group (the body decides group drawer vs single-signal drawer). */
  onActivate: (group: LocationGroup) => void
  /** Registers the dot element so the scan loop can flare it as it passes. */
  registerDot: (el: HTMLButtonElement | null) => void
  /**
   * GDELT dots mount hidden so a marker that appears mid-sweep (a filter refetch
   * landing during a pass) stays invisible until the line reveals it, instead of
   * flashing at full opacity for the frame before the rAF loop styles it.
   */
  initiallyHidden?: boolean
}> = ({
  group,
  selected,
  threshold,
  onActivate,
  registerDot,
  initiallyHidden,
}) => {
  const { color, locationLabel } = group
  // Show only the signals matching the active impact floor (the full group also
  // carries the sub-threshold "noise" that drives the sweep reveal).
  const passing =
    threshold == null
      ? group.signals
      : group.signals.filter(signal => getImpact(signal) >= threshold)
  const count = passing.length
  // When the active floor filters out every signal here, the rAF loop fades the
  // dot to opacity 0; keep it out of the a11y tree too so a screen reader does
  // not announce a "0 signals in …" marker the user can neither see nor click.
  const hidden = count === 0
  const diseases = summarizeDiseases(passing)
  // The hover label is hover-only; a click dismisses it (it would otherwise
  // linger via :hover while the cursor sits on the just-clicked dot). Reset on
  // mouse-out so a later hover shows it again.
  const [suppressLabel, setSuppressLabel] = useState(false)
  return (
    <Box
      onMouseLeave={() => setSuppressLabel(false)}
      sx={{
        position: "relative",
        width: 0,
        height: 0,
        cursor: "pointer",
        "&:hover .mk-label": { opacity: suppressLabel ? 0 : 1 },
        "&:hover": { zIndex: 20 },
      }}
    >
      <Box
        component="button"
        ref={registerDot}
        onClick={() => {
          setSuppressLabel(true)
          onActivate(group)
        }}
        aria-hidden={hidden || undefined}
        tabIndex={hidden ? -1 : undefined}
        aria-label={`${count} signal${count === 1 ? "" : "s"} in ${locationLabel}`}
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          // transform/filter are mutated by the scan loop to flare on sweep.
          transform: "translate(-50%, -50%)",
          width: 11,
          height: 11,
          borderRadius: "50%",
          padding: 0,
          cursor: "pointer",
          border: "1px solid rgba(255,255,255,0.25)",
          backgroundColor: color,
          // Initial opacity only — the rAF loop sets an inline opacity (which
          // wins over this) on its first frame. Hidden for GDELT so freshly
          // mounted dots don't flash before the sweep reaches them.
          opacity: initiallyHidden ? 0 : 1,
          boxShadow: selected
            ? `0 0 16px ${color}, 0 0 0 3px rgba(255,255,255,0.20)`
            : `0 0 8px ${color}`,
          // The scan loop owns every animated property per-frame (opacity,
          // color, glow, scale, filter), so no CSS transition here — a
          // transition on the same props would double-animate the fades.
          willChange:
            "transform, filter, opacity, background-color, box-shadow",
        }}
      />
      {selected ? (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            transform: "translate(-50%, -50%) scale(2.2)",
            width: 11,
            height: 11,
            borderRadius: "50%",
            border: `2px solid ${color}`,
            opacity: 0.5,
            pointerEvents: "none",
          }}
        />
      ) : null}
      <Box
        className="mk-label"
        sx={{
          position: "absolute",
          top: -7,
          left: 10,
          display: "flex",
          flexDirection: "column",
          gap: "1px",
          px: "7px",
          py: "3px",
          background: "rgba(8,11,17,0.94)",
          border: `1px solid ${color}`,
          borderRadius: "3px",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          // Hover-only — never pinned open (e.g. by selection); the container's
          // :hover rule reveals it, and a click suppresses it.
          opacity: 0,
          transition: "opacity .12s",
          zIndex: 6,
        }}
      >
        <Box
          component="span"
          sx={{ fontSize: 12, color: "#fff", fontWeight: 600, mb: "2px" }}
        >
          {locationLabel}
        </Box>
        {diseases.map(disease => (
          <Box
            key={disease.label}
            component="span"
            sx={{
              fontFamily: V2_FONT_MONO,
              fontSize: 11,
              color,
              letterSpacing: "0.04em",
              textTransform: "capitalize",
            }}
          >
            {disease.label} ({disease.count})
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export const BiothreatV2ScanMap: React.FC<{
  rows: BiothreatSignal[]
  kind: PrecomputedTab
  selectedId: string | null | undefined
  /** Key of the location group whose detail drawer is open (if any). */
  selectedGroupKey?: string | null
  /** Opens a dot's group — the body routes to a group or single-signal drawer. */
  onSelectGroup: (group: LocationGroup) => void
  /** Plate height in px; the body enlarges this when the app nav is collapsed. */
  height?: number
  /** Current rail width; changes drive an immediate GL canvas resize. */
  containerWidth?: number
  /** Reports when an on-demand pass is mid-flight (drives the status line). */
  onScanningChange?: (scanning: boolean) => void
}> = ({
  rows,
  kind,
  selectedId,
  selectedGroupKey,
  onSelectGroup,
  height = 340,
  containerWidth,
  onScanningChange,
}) => {
  const isNwss = kind === "nwss"
  const isGdelt = kind === "gdelt"
  const { gdeltFilters, scanMode } = usePrecomputedBiothreatSignals()
  // GDELT only: the sweep reveals signal (impact >= threshold) from the full
  // noise field. The threshold is the shared impact-score floor the slider sets,
  // and also the cutoff for what a dot's hover/drawer should show.
  const threshold = isGdelt
    ? getGdeltImpactFloor(gdeltFilters.impactScoreMin)
    : null
  const mapRef = useRef<MapRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const sweepRef = useRef<HTMLDivElement>(null)
  const sweepGlowRef = useRef<HTMLDivElement>(null)
  const dotRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const sweepProgress = useRef(0)

  // Two-state transition model. The sweep renders the diff between the PREVIOUS
  // filtered state and the CURRENT one: a dot is staying (blue), coming in
  // (green->blue), or leaving (red, fades out) depending on which states it
  // passes in. Membership = present in that fetch AND impact >= that threshold.
  const prevRowsRef = useRef<BiothreatSignal[]>([])
  const currRowsRef = useRef<BiothreatSignal[]>([])
  const prevIdsRef = useRef<Set<string>>(new Set())
  const currIdsRef = useRef<Set<string>>(new Set())
  const prevThresholdRef = useRef(0)
  const currThresholdRef = useRef(Number.NaN)
  const transitionRef = useRef<"idle" | "sweeping">("idle")
  // Distance (in sweep-widths) the line has traveled since the transition began;
  // a dot resolves once the line reaches it, the pass commits once it hits 1.
  const travelRef = useRef(0)
  // Line position when the transition began (auto starts mid-stride; on-demand
  // resets to the left edge), so crossing math handles the wrap correctly.
  const startProgressRef = useRef(0)
  const initializedRef = useRef(false)

  // Live mirrors so the rAF loop reads the latest without restarting on change.
  const scanModeRef = useRef<PrecomputedScanMode>(scanMode)
  const selectedIdRef = useRef(selectedId)
  const selectedGroupKeyRef = useRef(selectedGroupKey)
  const onScanningRef = useRef(onScanningChange)
  useEffect(() => {
    scanModeRef.current = scanMode
  }, [scanMode])
  useEffect(() => {
    selectedIdRef.current = selectedId
  }, [selectedId])
  useEffect(() => {
    selectedGroupKeyRef.current = selectedGroupKey
  }, [selectedGroupKey])
  useEffect(() => {
    onScanningRef.current = onScanningChange
  }, [onScanningChange])

  // Markers rendered = union of the two states during a transition (so leaving
  // dots stay mounted long enough to fade out), collapsing to current at commit.
  const [displayRows, setDisplayRows] = useState<BiothreatSignal[]>(rows)
  // The transition effect can't call setState synchronously (cascading renders);
  // it parks the next render set here and the rAF loop flushes it on its frame.
  const pendingRenderRef = useRef<BiothreatSignal[] | null>(null)

  // Resize the GL canvas whenever the plate's size changes — height (nav
  // collapse) or width (dragging the rail divider). react-map-gl's own
  // ResizeObserver lags behind a fast drag, so we resize explicitly each frame.
  useEffect(() => {
    const frame = requestAnimationFrame(() => mapRef.current?.resize())
    return () => cancelAnimationFrame(frame)
  }, [height, containerWidth])

  // Aggregate signals by resolved centroid so co-located clusters (e.g. every
  // US-national cluster) render as one marker instead of stacking — keeps the
  // map readable and performant when reflecting the full filtered set.
  const groups = useMemo<LocationGroup[]>(
    () => groupSignalsByLocation(displayRows, kind),
    [displayRows, kind]
  )

  // Signals whose location text resolves to no centroid (e.g. an "Unknown"
  // GDELT location) can't be plotted, so `groupSignalsByLocation` drops them.
  // Count them off the current filtered set — the same denominator as the RAW
  // count — and surface it in the HUD so the marker total and the count agree.
  const unplaceableCount = useMemo(
    () => rows.filter(row => !resolveSignalLatLon(row, kind)).length,
    [rows, kind]
  )

  // Collapse a finished transition: current becomes the new baseline and the
  // render set drops the just-faded leavers. Called once at the end of a pass.
  const commit = useCallback(() => {
    if (transitionRef.current !== "sweeping") return
    prevRowsRef.current = currRowsRef.current
    prevThresholdRef.current = currThresholdRef.current
    prevIdsRef.current = currIdsRef.current
    transitionRef.current = "idle"
    onScanningRef.current?.(false)
    setDisplayRows(currRowsRef.current)
  }, [])

  // Detect a new filtered/threshold state and arm a sweep to reveal the diff.
  // NWSS has no threshold model, so it just mirrors its rows.
  useEffect(() => {
    if (!isGdelt) {
      currRowsRef.current = rows
      pendingRenderRef.current = rows
      return
    }
    const nextThreshold = threshold ?? 0
    const rowsChanged = rows !== currRowsRef.current
    const thresholdChanged = nextThreshold !== currThresholdRef.current
    if (!rowsChanged && !thresholdChanged) return

    if (!initializedRef.current) {
      // First load: treat the whole field as prior "noise" (everything visible)
      // so the opening sweep burns sub-threshold dots red with no green flash.
      prevRowsRef.current = rows
      prevThresholdRef.current = 0
      if (rows.length > 0) initializedRef.current = true
    } else {
      prevRowsRef.current = currRowsRef.current
      prevThresholdRef.current = currThresholdRef.current
    }
    currRowsRef.current = rows
    currThresholdRef.current = nextThreshold
    prevIdsRef.current = idSetOf(prevRowsRef.current)
    currIdsRef.current = idSetOf(currRowsRef.current)

    const mode = scanModeRef.current
    travelRef.current = 0
    startProgressRef.current = mode === "onDemand" ? 0 : sweepProgress.current
    if (mode === "onDemand") sweepProgress.current = 0
    transitionRef.current = "sweeping"
    onScanningRef.current?.(true)
    pendingRenderRef.current = unionRows(
      prevRowsRef.current,
      currRowsRef.current
    )
  }, [rows, threshold, isGdelt])

  // Static reduced-motion paint: show the resolved CURRENT state directly (no
  // sweep, no green/red flashes). Re-runs on filter/threshold/selection change.
  useEffect(() => {
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (!reduceMotion) return
    if (sweepRef.current) sweepRef.current.style.opacity = "0"
    for (const group of groups) {
      const el = dotRefs.current[group.key]
      if (!el) continue
      if (!isGdelt) {
        clearDotStyles(el)
        continue
      }
      const selected =
        (selectedId != null && group.signalIds.includes(selectedId)) ||
        (selectedGroupKey != null && group.key === selectedGroupKey)
      const present = group.signalIds.some(id => currIdsRef.current.has(id))
      const passing = present && getImpact(group.lead) >= (threshold ?? 0)
      styleDot(el, {
        opacity: selected || passing ? 1 : 0,
        scale: 1,
        filter: "",
      })
    }
  }, [groups, isGdelt, threshold, selectedId, selectedGroupKey])

  // Drive the scan line and the marker reveal from one rAF loop. GDELT renders
  // the previous->current diff as the line passes each dot (staying = blue,
  // coming = green->blue, leaving = red->gone); NWSS keeps its ornamental flare.
  // Threshold/selection/scan-mode are read from refs so the loop never restarts
  // (no field flash, no line jump); only a new marker set restarts it.
  useEffect(() => {
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduceMotion) return

    const present = (group: LocationGroup, ids: Set<string>): boolean =>
      group.signalIds.some(id => ids.has(id))

    const applyReveal = (
      el: HTMLButtonElement,
      group: LocationGroup,
      screenX: number,
      width: number,
      sweepX: number,
      periodS: number
    ): void => {
      const flareWindow = Math.max(8, width * 0.01)
      const pulse = (): { scale: number; filter: string } => {
        const distance = Math.abs(screenX - sweepX)
        const flare = distance < flareWindow ? 1 - distance / flareWindow : 0
        return {
          scale: 1 + flare * 0.25,
          filter: `brightness(${(1 + flare * 0.45).toFixed(3)})`,
        }
      }

      // The selected dot stays pinned visible (never burned off or hidden by
      // the threshold), but still flares as the line crosses it — so it lights
      // up with the sweep like every other dot instead of sitting frozen.
      if (
        (selectedIdRef.current != null &&
          group.signalIds.includes(selectedIdRef.current)) ||
        (selectedGroupKeyRef.current != null &&
          group.key === selectedGroupKeyRef.current)
      ) {
        styleDot(el, { opacity: 1, ...pulse() })
        return
      }

      const passingPrev =
        present(group, prevIdsRef.current) &&
        getImpact(group.lead) >= prevThresholdRef.current
      const passingCurr =
        present(group, currIdsRef.current) &&
        getImpact(group.lead) >= currThresholdRef.current

      // Settled: paint the resolved current state (signals pulse, rest hidden).
      if (transitionRef.current !== "sweeping") {
        if (passingCurr) styleDot(el, { opacity: 1, ...pulse() })
        else styleDot(el, { opacity: 0, scale: 1, filter: "" })
        return
      }

      // Mid-sweep: has the line reached this dot since the transition began?
      const frac = Math.min(1, Math.max(0, width === 0 ? 0 : screenX / width))
      const dist =
        scanModeRef.current === "onDemand" || !isGdelt
          ? frac
          : (frac - startProgressRef.current + 1) % 1
      if (travelRef.current < dist) {
        // Ahead of the line — still showing the PREVIOUS state.
        if (passingPrev) styleDot(el, { opacity: 1, scale: 1, filter: "" })
        else styleDot(el, { opacity: 0, scale: 1, filter: "" })
        return
      }

      const elapsed = (travelRef.current - dist) * periodS * 1000
      if (passingPrev && passingCurr) {
        // Staying.
        styleDot(el, { opacity: 1, ...pulse() })
      } else if (!passingPrev && passingCurr) {
        // Coming in: fade in, green then settling to severity blue.
        const opacity = easeOutCubic(Math.min(1, elapsed / ENTRANCE_FADE_MS))
        let background: string
        let boxShadow: string
        if (elapsed < ENTRANCE_HOLD_MS) {
          background = V2_ACCENT.green
          boxShadow = `0 0 10px ${V2_ACCENT.green}`
        } else {
          const t = easeOutCubic(
            Math.min(1, (elapsed - ENTRANCE_HOLD_MS) / ENTRANCE_FADE_MS)
          )
          background = lerpColor(V2_ACCENT.green, group.color, t)
          boxShadow = `0 0 8px ${background}`
        }
        styleDot(el, { opacity, scale: 1, filter: "", background, boxShadow })
      } else if (passingPrev && !passingCurr) {
        // Leaving: red flash, then fade out.
        const opacity =
          elapsed < REJECT_HOLD_MS
            ? 1
            : 1 -
              easeOutCubic(
                Math.min(1, (elapsed - REJECT_HOLD_MS) / REJECT_FADE_MS)
              )
        styleDot(el, {
          opacity,
          scale: 1,
          filter: "",
          background: V2_ACCENT.reject,
          boxShadow: `0 0 10px ${V2_ACCENT.reject}`,
        })
      } else {
        // In neither state — stay hidden.
        styleDot(el, { opacity: 0, scale: 1, filter: "" })
      }
    }

    let raf = 0
    let last: number | null = null
    const loop = (time: number) => {
      if (last === null) last = time
      const dt = Math.min(0.05, (time - last) / 1000)
      last = time

      // Flush a render set parked by the transition effect (keeps setState out
      // of the effect body — the new markers mount and this loop restarts).
      if (pendingRenderRef.current) {
        const next = pendingRenderRef.current
        pendingRenderRef.current = null
        setDisplayRows(next)
      }

      const width = containerRef.current?.clientWidth ?? 0
      const mode = isGdelt ? scanModeRef.current : "auto"
      // On-demand runs a quick search pass; auto is the slow ambient sweep.
      const periodS =
        mode === "onDemand" ? ON_DEMAND_SWEEP_PERIOD_S : SWEEP_PERIOD_S

      // Advance the sweep + decide when the pass is done. Auto sweeps forever;
      // on-demand runs a single left->right pass then parks at the left edge.
      let parked = false
      if (mode === "auto") {
        sweepProgress.current = (sweepProgress.current + dt / periodS) % 1
        if (transitionRef.current === "sweeping") {
          travelRef.current += dt / periodS
          if (travelRef.current >= 1) commit()
        }
      } else if (transitionRef.current === "sweeping") {
        travelRef.current += dt / periodS
        sweepProgress.current = Math.min(1, travelRef.current)
        if (travelRef.current >= 1) {
          commit()
          sweepProgress.current = 0
        }
      } else {
        parked = true
        sweepProgress.current = 0
      }

      const sweepX = sweepProgress.current * width
      if (sweepRef.current) {
        if (parked) {
          // Parked at the left edge, breathing gently to read as "armed".
          sweepRef.current.style.left = "0%"
          sweepRef.current.style.opacity = (
            0.28 +
            0.22 * (0.5 + 0.5 * Math.sin(time / 480))
          ).toFixed(3)
        } else {
          sweepRef.current.style.left = `${(sweepProgress.current * 100).toFixed(2)}%`
          sweepRef.current.style.opacity = "0.5"
        }
      }
      // Searching glow only while an on-demand pass is actively sweeping. Fade
      // it in on activation, but cut it instantly when the pass ends so it never
      // trails along as the bar snaps back to the left to loop around.
      if (sweepGlowRef.current) {
        const searching =
          mode === "onDemand" && transitionRef.current === "sweeping"
        sweepGlowRef.current.style.transition = searching
          ? "opacity 0.25s ease"
          : "none"
        sweepGlowRef.current.style.opacity = searching ? "1" : "0"
      }

      const map = mapRef.current
      if (map) {
        for (const group of groups) {
          const el = dotRefs.current[group.key]
          if (!el) continue
          const screenX = map.project([group.lon, group.lat]).x

          if (isGdelt) {
            applyReveal(el, group, screenX, width, sweepX, periodS)
            continue
          }

          // NWSS — ornamental flare as the line passes.
          const flareWindow = Math.max(8, width * 0.01)
          const distance = Math.abs(screenX - sweepX)
          const flare = distance < flareWindow ? 1 - distance / flareWindow : 0
          el.style.transform = `translate(-50%, -50%) scale(${(1 + flare * 0.3).toFixed(3)})`
          el.style.filter = `brightness(${(1 + flare * 0.45).toFixed(3)})`
        }
      }
      raf = requestAnimationFrame(loop)
    }

    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [groups, isGdelt, commit])

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        height,
        flexShrink: 0,
        overflow: "hidden",
        borderRadius: "8px",
        border: `1px solid ${V2_SURFACE.hairline}`,
        backgroundColor: V2_SURFACE.app,
        // Keep the cyan command-console treatment over Mapbox's chrome.
        "& .mapboxgl-ctrl-group": {
          background: "rgba(8,11,17,0.85)",
          border: `1px solid ${V2_SURFACE.hairlineStrong}`,
        },
        "& .mapboxgl-ctrl-group button + button": {
          borderTop: `1px solid ${V2_SURFACE.hairline}`,
        },
        "& .mapboxgl-ctrl-icon": { filter: "invert(1) hue-rotate(180deg)" },
        "& .mapboxgl-ctrl-logo, & .mapboxgl-ctrl-attrib": { opacity: 0.5 },
        // Markers get an inline z-index from Mapbox (overlap ordering), so use
        // !important to lift the hovered/selected one above other dots — its
        // label would otherwise render under neighboring markers.
        "& .mapboxgl-marker:hover": { zIndex: "10 !important" },
        "& .mapboxgl-marker.mk-active": { zIndex: "9 !important" },
      }}
    >
      <MapGl
        ref={mapRef}
        // Remount on tab change so the view recentres to the new scope.
        key={kind}
        reuseMaps
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        mapStyle={MAP_STYLE}
        projection="mercator"
        initialViewState={isNwss ? CONUS_VIEW : WORLD_VIEW}
        minZoom={0}
        style={{ width: "100%", height: "100%" }}
      >
        <NavigationControl position="bottom-right" showCompass={false} />
        {groups.map(group => {
          const selected =
            (selectedId != null && group.signalIds.includes(selectedId)) ||
            (selectedGroupKey != null && group.key === selectedGroupKey)
          return (
            <Marker
              key={group.key}
              longitude={group.lon}
              latitude={group.lat}
              anchor="center"
              className={selected ? "mk-active" : undefined}
            >
              <LocationMarker
                group={group}
                selected={selected}
                threshold={threshold}
                onActivate={onSelectGroup}
                initiallyHidden={isGdelt}
                registerDot={el => {
                  dotRefs.current[group.key] = el
                }}
              />
            </Marker>
          )
        })}
      </MapGl>

      {/* Left→right scan sweep — position driven by the rAF loop above, which
          also flares the dots as the line crosses them. The trailing glow fans
          out to the right of the bar during on-demand passes, as if the line is
          actively searching the field ahead of it for dots. */}
      <Box
        ref={sweepRef}
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: 2,
          backgroundColor: V2_ACCENT.cyan,
          boxShadow: `0 0 12px 1px ${V2_ACCENT.cyan}`,
          opacity: 0.5,
          zIndex: 3,
          pointerEvents: "none",
        }}
      >
        <Box
          ref={sweepGlowRef}
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "2px",
            width: 84,
            background: SWEEP_GLOW,
            opacity: 0,
            transition: "opacity 0.25s ease",
            pointerEvents: "none",
          }}
        />
      </Box>

      <Hud sx={{ top: 8, right: 12 }}>
        {`${rows.length} ${isGdelt ? "RAW" : "CONTACTS"}`}
      </Hud>
      {unplaceableCount > 0 && (
        <Hud sx={{ top: 26, right: 12, fontSize: 11, color: V2_TEXT.disabled }}>
          {`${unplaceableCount} UNPLACED`}
        </Hud>
      )}
    </Box>
  )
}

const SCAN_MODES: { mode: PrecomputedScanMode; label: string }[] = [
  { mode: "auto", label: "AUTO" },
  { mode: "onDemand", label: "ON-DEMAND" },
]

/**
 * Scan-map controls shown below the plate: the AUTO/ON-DEMAND mode toggle
 * (GDELT only) plus the live sweep status. `scanning` reflects an in-flight
 * on-demand pass, surfaced from the map's animation loop.
 */
export const BiothreatV2ScanControls: React.FC<{
  kind: PrecomputedTab
  scanning: boolean
}> = ({ kind, scanning }) => {
  const { scanMode, setScanMode } = usePrecomputedBiothreatSignals()
  const isGdelt = kind === "gdelt"
  // NWSS has no transition model, so it always reads as a live continuous sweep.
  const onDemandIdle = isGdelt && scanMode === "onDemand" && !scanning
  const statusColor = onDemandIdle ? V2_TEXT.muted : V2_ACCENT.green
  const statusLabel = !isGdelt
    ? "SCAN SWEEP · ACTIVE"
    : scanMode === "auto"
      ? "SCAN SWEEP · ACTIVE"
      : scanning
        ? "SCANNING…"
        : "SCAN · IDLE"

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      {isGdelt ? (
        <Box
          role="radiogroup"
          aria-label="Scan mode"
          sx={{
            display: "flex",
            border: `1px solid ${V2_SURFACE.hairlineStrong}`,
            borderRadius: "7px",
            overflow: "hidden",
          }}
        >
          {SCAN_MODES.map(({ mode, label }) => {
            const active = scanMode === mode
            return (
              <Box
                key={mode}
                component="button"
                role="radio"
                aria-checked={active}
                onClick={() => setScanMode(mode)}
                sx={{
                  cursor: "pointer",
                  border: "none",
                  px: "12px",
                  py: "6px",
                  fontFamily: V2_FONT_MONO,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  backgroundColor: active ? V2_ACCENT.cyan : "transparent",
                  color: active ? V2_ON_ACCENT : V2_TEXT.muted,
                  transition: "background-color .12s, color .12s",
                  "&:hover": { color: active ? V2_ON_ACCENT : "#fff" },
                }}
              >
                {label}
              </Box>
            )
          })}
        </Box>
      ) : (
        <Box />
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            backgroundColor: statusColor,
            animation: onDemandIdle
              ? "none"
              : "biothreatScanPulse 1.6s ease-in-out infinite",
            "@keyframes biothreatScanPulse": {
              "0%, 100%": {
                opacity: 1,
                boxShadow: `0 0 0 0 ${V2_ACCENT.green}`,
              },
              "50%": {
                opacity: 0.4,
                boxShadow: `0 0 7px 3px ${V2_ACCENT.green}`,
              },
            },
          }}
        />
        <Typography
          sx={{
            fontFamily: V2_FONT_MONO,
            fontSize: 12,
            letterSpacing: "0.1em",
            fontWeight: 600,
            color: statusColor,
          }}
        >
          {statusLabel}
        </Typography>
      </Box>
    </Box>
  )
}
